import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Clock, CheckCircle, XCircle, AlertCircle, TrendingUp,
  Calendar, Users, RefreshCw, Download, Filter
} from 'lucide-react';
import './AttendanceTab.css';

const RAW_API_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_URL = RAW_API_URL.replace(/\/+$/, '').endsWith('/api')
  ? RAW_API_URL.replace(/\/+$/, '')
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

const AttendanceTab = ({ hotelId, token }) => {
  const [loading, setLoading] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState(null);
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [departments, setDepartments] = useState([]);

  console.log('[AttendanceTab] Props:', { hotelId, hasToken: !!token, tokenLength: token?.length });

  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  useEffect(() => {
    if (token && hotelId) {
      // fetchRealtimeStatus(); // TODO: Implement backend endpoint
      fetchDepartments();
      fetchMonthlyReport();
      // Refresh every 60 seconds
      // const interval = setInterval(fetchRealtimeStatus, 60000);
      // return () => clearInterval(interval);
    }
  }, [token, hotelId]);

  const fetchRealtimeStatus = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/attendance/hotel/${hotelId}/realtime`,
        auth
      );
      if (res.data.success) {
        setRealtimeStatus(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching realtime status:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/departments`,
        auth
      );
      if (res.data.success) {
        setDepartments(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchMonthlyReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear
      });
      if (departmentFilter) {
        params.append('department_id', departmentFilter);
      }

      const res = await axios.get(
        `${API_URL}/attendance/hotel/${hotelId}/report?${params}`,
        auth
      );
      if (res.data.success) {
        setAttendanceReport(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching attendance report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = () => {
    // TODO: Open manual entry modal
    alert('Manual attendance entry feature - to be implemented');
  };

  const handleExportReport = () => {
    // TODO: Export to CSV/Excel
    alert('Export report feature - to be implemented');
  };

  return (
    <div className="attendance-tab">
      {/* Real-time Status Dashboard */}
      <div className="content-card">
        <div className="card-header">
          <h2>
            <Clock size={24} />
            Today's Attendance
          </h2>
          <button className="btn-icon" onClick={fetchRealtimeStatus} title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>

        {realtimeStatus && (
          <div className="realtime-stats">
            <div className="stat-card present">
              <div className="stat-icon">
                <CheckCircle size={32} />
              </div>
              <div className="stat-info">
                <h3>{realtimeStatus.checked_in || 0}</h3>
                <p>Checked In</p>
              </div>
            </div>

            <div className="stat-card absent">
              <div className="stat-icon">
                <XCircle size={32} />
              </div>
              <div className="stat-info">
                <h3>{realtimeStatus.absent || 0}</h3>
                <p>Absent</p>
              </div>
            </div>

            <div className="stat-card late">
              <div className="stat-icon">
                <AlertCircle size={32} />
              </div>
              <div className="stat-info">
                <h3>{realtimeStatus.late_arrivals || 0}</h3>
                <p>Late Arrivals</p>
              </div>
            </div>

            <div className="stat-card total">
              <div className="stat-icon">
                <Users size={32} />
              </div>
              <div className="stat-info">
                <h3>{realtimeStatus.total_staff || 0}</h3>
                <p>Total Staff</p>
              </div>
            </div>
          </div>
        )}

        {realtimeStatus && realtimeStatus.currently_checked_in && (
          <div className="checked-in-list">
            <h3>Currently Checked In</h3>
            <div className="staff-list">
              {realtimeStatus.currently_checked_in.map(staff => (
                <div key={staff.attendance_id} className="staff-item">
                  <div className="staff-avatar">
                    {staff.full_name?.charAt(0) || 'S'}
                  </div>
                  <div className="staff-details">
                    <p className="staff-name">{staff.full_name}</p>
                    <p className="staff-department">{staff.department_name || 'N/A'}</p>
                  </div>
                  <div className="staff-time">
                    <span className="check-in-time">
                      In: {new Date(staff.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {staff.is_late && <span className="late-badge">Late</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Monthly Report */}
      <div className="content-card">
        <div className="card-header">
          <h2>
            <TrendingUp size={24} />
            Monthly Attendance Report
          </h2>
          <div className="card-actions">
            <button className="btn-secondary" onClick={handleManualEntry}>
              <Clock size={18} />
              Manual Entry
            </button>
            <button className="btn-secondary" onClick={handleExportReport}>
              <Download size={18} />
              Export
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="report-filters">
          <div className="filter-group">
            <label>
              <Calendar size={16} />
              Month
            </label>
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(parseInt(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Year</label>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => (
                <option key={2026 - i} value={2026 - i}>
                  {2026 - i}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>
              <Filter size={16} />
              Department
            </label>
            <select
              value={departmentFilter}
              onChange={e => setDepartmentFilter(e.target.value)}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name}
                </option>
              ))}
            </select>
          </div>

          <button className="btn-primary" onClick={fetchMonthlyReport}>
            Generate Report
          </button>
        </div>

        {/* Report Summary */}
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading report...</p>
          </div>
        ) : attendanceReport && (
          <div className="report-summary">
            <div className="summary-grid">
              <div className="summary-item">
                <label>Total Staff</label>
                <span className="value">{attendanceReport.total_staff || 0}</span>
              </div>
              <div className="summary-item">
                <label>Present Today</label>
                <span className="value success">{attendanceReport.present_today || 0}</span>
              </div>
              <div className="summary-item">
                <label>Absent Today</label>
                <span className="value error">{attendanceReport.absent_today || 0}</span>
              </div>
              <div className="summary-item">
                <label>Late Arrivals</label>
                <span className="value warning">{attendanceReport.late_arrivals || 0}</span>
              </div>
              <div className="summary-item">
                <label>Attendance Rate</label>
                <span className="value info">{attendanceReport.average_attendance_rate || 0}%</span>
              </div>
              <div className="summary-item">
                <label>Total Working Hours</label>
                <span className="value">{attendanceReport.total_working_hours || 0} hrs</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="content-card">
        <h2>Quick Actions</h2>
        <div className="quick-actions-grid">
          <button className="action-card" onClick={handleManualEntry}>
            <Clock size={32} />
            <h3>Manual Entry</h3>
            <p>Add attendance record manually</p>
          </button>
          <button className="action-card" onClick={() => alert('Approve pending - to be implemented')}>
            <CheckCircle size={32} />
            <h3>Approve Pending</h3>
            <p>Review and approve attendance</p>
          </button>
          <button className="action-card" onClick={() => alert('View history - to be implemented')}>
            <Calendar size={32} />
            <h3>View History</h3>
            <p>Browse attendance history</p>
          </button>
          <button className="action-card" onClick={handleExportReport}>
            <Download size={32} />
            <h3>Export Data</h3>
            <p>Download reports as CSV/Excel</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceTab;
